import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  TablePagination,
  TableSortLabel,
  keyframes,
} from "@mui/material";
import {
  Visibility as ViewIcon,
  Edit as EditIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Delete as DeleteIcon,
  Publish as PublishIcon,
  Archive as ArchiveIcon,
} from "@mui/icons-material";
import { api } from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import { NewsArticle } from "../../types";
import { formatDate } from "../../utils/dateFormat";
import NewsSubmission from "../reporter/NewsSubmission";
import log from "../../utils/logger";

// Highlight animation for new/updated items
const highlightFade = keyframes`
  0% {
    background-color: rgba(25, 118, 210, 0.2);
  }
  100% {
    background-color: transparent;
  }
`;

// Helper function to get video embed URL
const getVideoEmbed = (url: string): { type: string; embedUrl: string } | null => {
  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname.toLowerCase();

    log.debug('Parsing video URL:', url, 'hostname:', hostname);

    // YouTube
    if (hostname.includes('youtube.com') || hostname.includes('youtu.be')) {
      let videoId = '';
      if (hostname.includes('youtu.be')) {
        // youtu.be/VIDEO_ID or youtu.be/VIDEO_ID?si=...
        videoId = urlObj.pathname.slice(1).split('/')[0];
      } else if (urlObj.pathname.includes('/embed/')) {
        videoId = urlObj.pathname.split('/embed/')[1].split('/')[0];
      } else if (urlObj.pathname.includes('/shorts/')) {
        videoId = urlObj.pathname.split('/shorts/')[1].split('/')[0];
      } else {
        videoId = urlObj.searchParams.get('v') || '';
      }
      log.debug('YouTube video ID:', videoId);
      if (videoId) {
        return { type: 'youtube', embedUrl: `https://www.youtube.com/embed/${videoId}` };
      }
    }

    // Instagram
    if (hostname.includes('instagram.com')) {
      const match = url.match(/instagram\.com\/(p|reel|reels|tv)\/([^/?]+)/);
      log.debug('Instagram match:', match);
      if (match) {
        return { type: 'instagram', embedUrl: `https://www.instagram.com/p/${match[2]}/embed` };
      }
    }

    // Facebook
    if (hostname.includes('facebook.com') || hostname.includes('fb.watch') || hostname.includes('fb.com')) {
      log.debug('Facebook video detected');
      return { type: 'facebook', embedUrl: `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url)}&show_text=false&width=560` };
    }

    log.debug('No video embed match found');
    return null;
  } catch (e) {
    log.error('Error parsing video URL:', e);
    return null;
  }
};

// Video embed component
const VideoEmbed: React.FC<{ url: string }> = ({ url }) => {
  const embed = getVideoEmbed(url);

  if (!embed) {
    return (
      <Typography variant="body2" sx={{ mb: 1 }}>
        <a href={url} target="_blank" rel="noopener noreferrer">{url}</a>
      </Typography>
    );
  }

  return (
    <Box sx={{ mb: 2, position: 'relative', paddingTop: '56.25%', width: '100%' }}>
      <iframe
        src={embed.embedUrl}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          border: 'none',
        }}
        allowFullScreen
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        title={`${embed.type} video`}
      />
    </Box>
  );
};

interface NewsDashboardProps {
  viewType?: "drafts" | "in-review" | "ready-to-publish" | "published";
  onNavigate?: (path: string) => void;
}

const NewsDashboard: React.FC<NewsDashboardProps> = ({ viewType, onNavigate }) => {
  const { user } = useAuth();
  const [news, setNews] = useState<NewsArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Pagination and sorting states (for published view)
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [orderBy, setOrderBy] = useState<"createdAt" | "publishedAt">("publishedAt");
  const [orderDirection, setOrderDirection] = useState<"asc" | "desc">("desc");

  // Dialog states
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [selectedNews, setSelectedNews] = useState<NewsArticle | null>(null);

  // Edit mode state - show NewsSubmission component instead of dialog
  const [editingNews, setEditingNews] = useState<NewsArticle | null>(null);

  const [updating, setUpdating] = useState(false);

  // Reject feedback
  const [rejectionFeedback, setRejectionFeedback] = useState("");

  const isAdmin = user?.role === "admin";
  const isChiefEditor = user?.role === "chief-editor" || isAdmin;
  const isCoEditor = user?.role === "co-editor" || isChiefEditor;
  const isReporter = user?.role === "reporter";

  const loadNews = useCallback(async () => {
    try {
      setLoading(true);
      let searchParams: any = {
        orderBy: "createdAt",
        orderDirection: "desc",
      };

      // Configure search based on view type
      switch (viewType) {
        case "drafts":
          // Drafts: draft + rejected status
          // Reporters see only their own, others see all
          if (isReporter) {
            searchParams.authorUid = user?.uid;
          }
          // We'll load draft and rejected separately and combine
          const [draftResult, rejectedResult] = await Promise.all([
            api.searchNews({ ...searchParams, status: "draft" }),
            api.searchNews({ ...searchParams, status: "rejected" }),
          ]);
          const combined = [
            ...(draftResult.news || []),
            ...(rejectedResult.news || []),
          ];
          setNews(combined as NewsArticle[]);
          setLoading(false);
          return;

        case "in-review":
          // In Review: in-review status
          // Reporters see only their own, others see all
          if (isReporter) {
            searchParams.authorUid = user?.uid;
          }
          searchParams.status = "in-review";
          break;

        case "ready-to-publish":
          // Ready to Publish: approved status
          // Reporters see only their own, others see all
          if (isReporter) {
            searchParams.authorUid = user?.uid;
          }
          searchParams.status = "approved";
          break;

        case "published":
          // Published: published status
          // All users see all published articles
          searchParams.status = "published";
          searchParams.orderBy = orderBy;
          searchParams.orderDirection = orderDirection;
          break;

        default:
          // Default to drafts
          if (isReporter) {
            searchParams.authorUid = user?.uid;
          }
          searchParams.status = "draft";
          break;
      }

      const result = await api.searchNews(searchParams);

      if (result.success && result.news) {
        log.debug(`Loaded ${result.news.length} articles for view: ${viewType}`, result.news);
        setNews(result.news as NewsArticle[]);
      } else {
        setError(result.error || "Failed to load news");
      }
    } catch (err: any) {
      setError(err.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  }, [viewType, user, isReporter, orderBy, orderDirection]);

  useEffect(() => {
    loadNews();
    // Reset to first page when data changes
    setPage(0);
  }, [loadNews]);

  const handleSort = (property: "createdAt" | "publishedAt") => {
    const isAsc = orderBy === property && orderDirection === "asc";
    setOrderDirection(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleView = (article: NewsArticle) => {
    log.debug('handleView called with article:', article);
    log.debug('article.videoLinks:', article.videoLinks);
    setSelectedNews(article);
    setViewDialogOpen(true);
  };

  const handleEdit = (article: NewsArticle) => {
    setEditingNews(article);
  };

  const handleEditSuccess = (navigateTo?: string) => {
    setSuccess("Article updated successfully");
    setEditingNews(null);
    loadNews();
    // Trigger heartbeat refresh
    if ((window as any).__reloadCounts) {
      (window as any).__reloadCounts();
    }
    // Navigate to the appropriate view
    if (navigateTo && onNavigate) {
      onNavigate(navigateTo);
    }
    setTimeout(() => setSuccess(null), 3000);
  };

  const handleCancelEdit = () => {
    setEditingNews(null);
  };

  const handleSubmitForReview = async (newsId: string) => {
    try {
      const result = await api.updateNews({
        newsId,
        status: "in-review",
      });

      if (result.success) {
        setSuccess("Submitted for review successfully");
        loadNews();
        // Trigger heartbeat refresh
        if ((window as any).__reloadCounts) {
          (window as any).__reloadCounts();
        }
        setTimeout(() => setSuccess(null), 3000);
        // Navigate to Awaiting Review page
        if (onNavigate) {
          onNavigate("in-review");
        }
      } else {
        setError(result.error || "Failed to submit for review");
      }
    } catch (err: any) {
      setError(err.message || "An error occurred");
    }
  };

  const handleApprove = async () => {
    if (!selectedNews) return;

    try {
      setUpdating(true);
      const result = await api.approveNews({
        newsId: selectedNews.id,
        approve: true,
      });

      if (result.success) {
        setApproveDialogOpen(false);
        setSuccess("Article approved successfully");
        loadNews();
        // Trigger heartbeat refresh
        if ((window as any).__reloadCounts) {
          (window as any).__reloadCounts();
        }
        setTimeout(() => setSuccess(null), 3000);
        // Navigate to Awaiting Publish page
        if (onNavigate) {
          onNavigate("ready-to-publish");
        }
      } else {
        setError(result.error || "Failed to approve article");
      }
    } catch (err: any) {
      setError(err.message || "An error occurred");
    } finally {
      setUpdating(false);
    }
  };

  const handleReject = async () => {
    if (!selectedNews) return;

    try {
      setUpdating(true);
      const result = await api.approveNews({
        newsId: selectedNews.id,
        approve: false,
        feedback: rejectionFeedback,
      });

      if (result.success) {
        setRejectDialogOpen(false);
        setRejectionFeedback("");
        setSuccess("Article rejected");
        loadNews();
        // Trigger heartbeat refresh
        if ((window as any).__reloadCounts) {
          (window as any).__reloadCounts();
        }
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(result.error || "Failed to reject article");
      }
    } catch (err: any) {
      setError(err.message || "An error occurred");
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedNews) return;

    try {
      setUpdating(true);
      const result = await api.deleteNews({
        newsId: selectedNews.id,
      });

      if (result.success) {
        setDeleteDialogOpen(false);
        setSuccess("Article deleted successfully");
        loadNews();
        // Trigger heartbeat refresh
        if ((window as any).__reloadCounts) {
          (window as any).__reloadCounts();
        }
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(result.error || "Failed to delete article");
      }
    } catch (err: any) {
      setError(err.message || "An error occurred");
    } finally {
      setUpdating(false);
    }
  };

  const handleArchive = async (articleId: string) => {
    if (!window.confirm("Are you sure you want to archive this article? It will be moved to the Archived Articles section.")) {
      return;
    }

    try {
      setUpdating(true);
      const result = await api.archiveNews(articleId);

      if (result.success) {
        setSuccess("Article archived successfully");
        loadNews();
        // Trigger heartbeat refresh
        if ((window as any).__reloadCounts) {
          (window as any).__reloadCounts();
        }
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(result.error || "Failed to archive article");
      }
    } catch (err: any) {
      setError(err.message || "An error occurred");
    } finally {
      setUpdating(false);
    }
  };

  const handlePublish = async () => {
    if (!selectedNews) return;

    try {
      setUpdating(true);
      const result = await api.publishNews({
        newsId: selectedNews.id,
      });

      if (result.success) {
        setPublishDialogOpen(false);
        setSuccess("Article published successfully");
        loadNews();
        // Trigger heartbeat refresh
        if ((window as any).__reloadCounts) {
          (window as any).__reloadCounts();
        }
        setTimeout(() => setSuccess(null), 3000);
        // Navigate to Published page
        if (onNavigate) {
          onNavigate("live");
        }
      } else {
        setError(result.error || "Failed to publish article");
      }
    } catch (err: any) {
      setError(err.message || "An error occurred");
    } finally {
      setUpdating(false);
    }
  };

  const canDeleteArticle = (article: NewsArticle): boolean => {
    if (isAdmin || isChiefEditor) {
      // Admin and Chief Editor can delete any article in any state
      return true;
    }
    // Others can only delete their own articles in draft or in-review status
    const isOwner = article.authorUid === user?.uid;
    const canDeleteStatus = article.status === "draft" || article.status === "in-review";
    return isOwner && canDeleteStatus;
  };

  const canEditArticle = (article: NewsArticle): boolean => {
    // Admin, Chief Editor, Co-Editor can edit any article in any state
    if (isAdmin || isChiefEditor || isCoEditor) {
      return true;
    }
    // Reporter can edit their own articles in any state
    const isOwner = article.authorUid === user?.uid;
    return isReporter && isOwner;
  };

  const getStatusColor = (
    status: string
  ): "default" | "primary" | "success" | "error" | "warning" | "info" => {
    switch (status) {
      case "draft":
        return "default";
      case "in-review":
        return "primary";
      case "approved":
        return "success";
      case "published":
        return "success";
      case "rejected":
        return "error";
      default:
        return "default";
    }
  };

  const renderActions = (article: NewsArticle) => {
    return (
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        <Button
          size="small"
          startIcon={<ViewIcon />}
          onClick={() => handleView(article)}
        >
          View
        </Button>

        {/* Drafts view actions */}
        {viewType === "drafts" && (
          <>
            {article.status === "draft" && (
              <>
                <Button
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => handleEdit(article)}
                >
                  Edit
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={() => handleSubmitForReview(article.id)}
                >
                  Submit
                </Button>
              </>
            )}
            {article.status === "rejected" && (
              <>
                <Chip label="Rejected" color="error" size="small" sx={{ mr: 1 }} />
                <Button
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => handleEdit(article)}
                >
                  Edit
                </Button>
              </>
            )}
          </>
        )}

        {/* In Review view actions */}
        {viewType === "in-review" && isCoEditor && (
          <>
            <Button
              size="small"
              startIcon={<EditIcon />}
              onClick={() => handleEdit(article)}
            >
              Edit
            </Button>
            <Button
              size="small"
              variant="contained"
              color="success"
              startIcon={<ApproveIcon />}
              onClick={() => {
                setSelectedNews(article);
                setApproveDialogOpen(true);
              }}
            >
              Approve
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<RejectIcon />}
              onClick={() => {
                setSelectedNews(article);
                setRejectDialogOpen(true);
              }}
            >
              Reject
            </Button>
          </>
        )}

        {/* Ready to Publish view - Edit and Publish actions */}
        {viewType === "ready-to-publish" && (
          <>
            {canEditArticle(article) && (
              <Button
                size="small"
                startIcon={<EditIcon />}
                onClick={() => handleEdit(article)}
              >
                Edit
              </Button>
            )}
            {isChiefEditor && (
              <Button
                size="small"
                variant="contained"
                color="primary"
                startIcon={<PublishIcon />}
                onClick={() => {
                  setSelectedNews(article);
                  setPublishDialogOpen(true);
                }}
              >
                Publish
              </Button>
            )}
          </>
        )}

        {/* Published view - Edit and Archive actions */}
        {viewType === "published" && (
          <>
            {canEditArticle(article) && (
              <Button
                size="small"
                startIcon={<EditIcon />}
                onClick={() => handleEdit(article)}
              >
                Edit
              </Button>
            )}
            {isCoEditor && (
              <Button
                size="small"
                color="warning"
                startIcon={<ArchiveIcon />}
                onClick={() => handleArchive(article.id)}
              >
                Archive
              </Button>
            )}
          </>
        )}

        {canDeleteArticle(article) && viewType !== "published" && (
          <Button
            size="small"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => {
              setSelectedNews(article);
              setDeleteDialogOpen(true);
            }}
          >
            Delete
          </Button>
        )}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  const getViewTitle = () => {
    switch (viewType) {
      case "drafts":
        return "Drafts";
      case "in-review":
        return "Awaiting Review";
      case "ready-to-publish":
        return "Awaiting Publish";
      case "published":
        return "Published";
      default:
        return "News";
    }
  };

  // Show NewsSubmission component when editing
  if (editingNews) {
    return (
      <Box>
        <Button
          onClick={handleCancelEdit}
          sx={{ mb: 2 }}
          variant="outlined"
        >
          ← Back to {getViewTitle()}
        </Button>
        <NewsSubmission
          editMode={true}
          newsId={editingNews.id}
          initialTitle={editingNews.title}
          initialSubtitle={editingNews.subtitle}
          initialContent={editingNews.content}
          initialImages={editingNews.images || []}
          initialVideoLinks={editingNews.videoLinks || []}
          onSuccess={handleEditSuccess}
        />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {getViewTitle()}
      </Typography>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Subtitle</TableCell>
              {viewType === "published" ? (
                <>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === "createdAt"}
                      direction={orderBy === "createdAt" ? orderDirection : "asc"}
                      onClick={() => handleSort("createdAt")}
                    >
                      Created
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === "publishedAt"}
                      direction={orderBy === "publishedAt" ? orderDirection : "asc"}
                      onClick={() => handleSort("publishedAt")}
                    >
                      Published
                    </TableSortLabel>
                  </TableCell>
                </>
              ) : (
                <TableCell>Created</TableCell>
              )}
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {news.length === 0 ? (
              <TableRow>
                <TableCell colSpan={viewType === "published" ? 5 : 4} align="center">
                  No articles found
                </TableCell>
              </TableRow>
            ) : (
              (viewType === "published"
                ? news.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                : news
              ).map((article) => {
                // Check if article was recently updated (within last 30 seconds)
                const updatedTime = article.updatedAt instanceof Date
                  ? article.updatedAt.getTime()
                  : new Date(article.updatedAt).getTime();
                const isRecent = Date.now() - updatedTime < 30000;

                return (
                <TableRow
                  key={article.id}
                  sx={isRecent ? {
                    animation: `${highlightFade} 5s ease-out forwards`,
                  } : undefined}
                >
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {article.title}
                      {(article as any).isEdit && (
                        <Chip
                          label="EDIT"
                          size="small"
                          color="info"
                          sx={{ fontSize: "0.65rem", height: 20 }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{article.subtitle}</TableCell>
                  <TableCell>{formatDate(article.createdAt)}</TableCell>
                  {viewType === "published" && (
                    <TableCell>{formatDate(article.publishedAt)}</TableCell>
                  )}
                  <TableCell>{renderActions(article)}</TableCell>
                </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        {viewType === "published" && news.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[25]}
            component="div"
            count={news.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </TableContainer>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{selectedNews?.title}</DialogTitle>
        <DialogContent>
          {/* Workflow Status Tracker */}
          <Box sx={{ mb: 3, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ mb: 2, fontWeight: "bold" }}>
              Workflow Progress
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              {["draft", "in-review", "approved", "published"].map((status, index) => {
                const isActive = selectedNews?.status === status;
                const isPast = ["draft", "in-review", "approved", "published"].indexOf(selectedNews?.status || "") > index;
                const isRejected = selectedNews?.status === "rejected";

                return (
                  <React.Fragment key={status}>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: "50%",
                          bgcolor: isRejected && status === "draft"
                            ? "error.main"
                            : isPast || isActive
                            ? "success.main"
                            : "grey.300",
                          color: "white",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "0.875rem",
                          fontWeight: "bold",
                        }}
                      >
                        {isPast || isActive ? "✓" : index + 1}
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          mt: 0.5,
                          fontWeight: isActive ? "bold" : "normal",
                          color: isActive ? "primary.main" : "text.secondary",
                          textTransform: "capitalize",
                        }}
                      >
                        {status.replace("-", " ")}
                      </Typography>
                    </Box>
                    {index < 3 && (
                      <Box
                        sx={{
                          flex: 1,
                          height: 2,
                          mx: 1,
                          bgcolor: isPast ? "success.main" : "grey.300",
                        }}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </Box>
            {selectedNews?.status === "rejected" && (
              <Box sx={{ mt: 2, p: 1.5, bgcolor: "error.50", borderRadius: 1, border: "1px solid", borderColor: "error.200" }}>
                <Typography variant="body2" color="error.dark" sx={{ fontWeight: "bold" }}>
                  Article Rejected
                </Typography>
                {selectedNews?.rejectionFeedback && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Feedback: {selectedNews.rejectionFeedback}
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          <Typography variant="h6" gutterBottom color="text.secondary">
            {selectedNews?.subtitle}
          </Typography>

          {selectedNews?.images && selectedNews.images.length > 0 && (
            <Box sx={{ my: 2 }}>
              {selectedNews.images.map((imageUrl, index) => (
                <Box
                  key={index}
                  component="img"
                  src={imageUrl}
                  alt={`Image ${index + 1}`}
                  sx={{
                    width: "100%",
                    maxHeight: 400,
                    objectFit: "contain",
                    mb: index < selectedNews.images!.length - 1 ? 2 : 0,
                    borderRadius: 1,
                  }}
                />
              ))}
            </Box>
          )}

          <Typography variant="body1" sx={{ whiteSpace: "pre-wrap", mt: 2 }}>
            {selectedNews?.content}
          </Typography>

          {/* Debug: show videoLinks status */}
          {(() => { log.debug('selectedNews.videoLinks:', selectedNews?.videoLinks); return null; })()}

          {selectedNews?.videoLinks && selectedNews.videoLinks.length > 0 ? (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Videos:
              </Typography>
              {selectedNews.videoLinks.map((link, index) => (
                <VideoEmbed key={index} url={link} />
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              No videos attached (videoLinks: {JSON.stringify(selectedNews?.videoLinks)})
            </Typography>
          )}

          <Box sx={{ mt: 2 }}>
            <Chip label={selectedNews?.status || "unknown"} color={getStatusColor(selectedNews?.status || "unknown")} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Approve Dialog */}
      <Dialog open={approveDialogOpen} onClose={() => setApproveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Approve Article</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to approve "{selectedNews?.title}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialogOpen(false)} disabled={updating}>
            Cancel
          </Button>
          <Button onClick={handleApprove} variant="contained" color="success" disabled={updating}>
            {updating ? "Processing..." : "Approve"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Article</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Are you sure you want to reject "{selectedNews?.title}"?
          </Typography>
          <TextField
            fullWidth
            label="Feedback (optional)"
            value={rejectionFeedback}
            onChange={(e) => setRejectionFeedback(e.target.value)}
            multiline
            rows={3}
            margin="normal"
            placeholder="Provide feedback for the author..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)} disabled={updating}>
            Cancel
          </Button>
          <Button onClick={handleReject} variant="contained" color="error" disabled={updating}>
            {updating ? "Processing..." : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Delete Article</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Are you sure you want to permanently delete "{selectedNews?.title}"?
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={updating}>
            Cancel
          </Button>
          <Button onClick={handleDelete} variant="contained" color="error" disabled={updating}>
            {updating ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Publish Dialog */}
      <Dialog open={publishDialogOpen} onClose={() => setPublishDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Publish Article</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to publish "{selectedNews?.title}"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This will make the article visible to the public.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPublishDialogOpen(false)} disabled={updating}>
            Cancel
          </Button>
          <Button onClick={handlePublish} variant="contained" color="primary" disabled={updating}>
            {updating ? "Publishing..." : "Publish"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NewsDashboard;
